import {
  Autocomplete,
  Box,
  CircularProgress,
  FilterOptionsState,
} from '@mui/material';
import React, { useCallback } from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface MultiAutocompleteInputProps<T> extends BaseInputProps {
  value: T[];
  onChange: (value: T[]) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  filterOptions?: (options: T[], inputValue: string) => T[];
  openOnFocus?: boolean;
  name?: string;
  size?: 'small' | 'medium';
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  loading?: boolean;
  loadingText?: React.ReactNode;
  noOptionsText?: string;
}

/**
 * Autocomplete multi-select con búsqueda.
 * - Genérico para cualquier tipo
 * - Filtrado personalizable
 * - Estilo consistente con otros inputs comunes
 */
export function MultiAutocompleteInput<T>({
  value,
  onChange,
  options,
  getOptionLabel,
  filterOptions,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  openOnFocus = true,
  name,
  size = 'small',
  isOptionEqualToValue,
  loading,
  loadingText = '',
  noOptionsText,
}: MultiAutocompleteInputProps<T>): React.ReactElement {
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: T[]) => {
      onChange(newValue);
    },
    [onChange],
  );

  const muiFilterOptions = filterOptions
    ? (options: T[], state: FilterOptionsState<T>) =>
        filterOptions(options, state.inputValue)
    : undefined;

  return (
    <Autocomplete
      multiple
      value={value}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      filterOptions={muiFilterOptions}
      disabled={disabled}
      fullWidth={fullWidth}
      openOnFocus={openOnFocus}
      size={size}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      filterSelectedOptions
      renderInput={(params) => {
        const originalEndAdornment = params.InputProps.endAdornment;

        return (
          <StyledTextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            required={required}
            name={name}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {loading ? <CircularProgress size={16} /> : null}
                  {originalEndAdornment}
                </Box>
              ),
            }}
          />
        );
      }}
    />
  );
}
